import { getFetcher } from '@/api/fetchers';
import {
  type CalendarEvent,
  type CalendarDay,
  Calendar,
} from '@/components/Calendar/Calendar';
import {
  type PlanningContextType,
  StateContext,
} from '@/layouts/Planning/PlanningLayout';
import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  useContext,
  type ReactElement,
  type ReactNode,
  useState,
  useEffect,
} from 'react';
import { AssignmentsModal } from '../Modals/AssignmentsModal';
import dayjs from 'dayjs';
import { badNotification } from '@/components/Notification/Notification';

interface Props {
  children: ReactNode[];
  planState?: string;
}

const fetchAllPages = async (url: string): Promise<any[]> => {
  let allData: any[] = [];
  let currentPage = 1;
  let last = false;

  while (!last) {
    const response = await getFetcher(`${url}?page=${currentPage}&size=10`);

    allData = allData.concat(response.content);
    currentPage = currentPage + 1;
    last = response.last;
  }

  return allData;
};

export const PlanningContainer = (props: Props): ReactElement => {
  const [
    openedAssignmentsModal,
    { open: openAssignmentsModal, close: closeAssignmentsModal },
  ] = useDisclosure(false);
  const { children, planState = 'DEFAULT' } = props;
  const {
    month,
    clientPlanId,
    planId,
    clientName,
    setClientPlanId,
    setClientName,
  } = useContext<PlanningContextType>(StateContext);

  // Přidáváme updateKey pro vynucení rerenderu
  const [updateKey, setUpdateKey] = useState(0);

  const [currTaskId, setCurrTaskId] = useState<number | undefined>(undefined); // holds an ID of the task that has been recently clicked
  const [clientSchedule, setClientSchedule] = useState<CalendarDay[]>([]);

  // Click handlery pro elementy scheduleMap
  const finishedClickHandler = () => badNotification('Vznikla chyba!', 'Plány z minulosti nelze zpětně editovat');
  const plannedClickHandler = () => badNotification('Vznikla chyba!', 'Klientský plán byl již naplánován');
  const defaultClickHandler = (id: number) => {
    setCurrTaskId(id);
    openAssignmentsModal();
  };

  const loadAllTasks = async () => {
    if (clientPlanId) {
      const allTasks = await fetchAllPages(
        `client-plans/${clientPlanId}/tasks`
      );
      setClientSchedule(convertToSchedule(allTasks, month));
      // Po načtení úkolů aktualizujeme klíč, aby se vše znovu vykreslilo
      setUpdateKey(prevKey => prevKey + 1);
    } else {
      setClientSchedule([]);
    }
  };

  useEffect(() => {
    loadAllTasks();
  }, [clientPlanId, month, planId]);

  useEffect(() => {
    return () => {
      setClientSchedule([]);
      setClientPlanId(undefined);
      setClientName(undefined);
    };
  }, []);

  const convertToSchedule = (
    data: any[],
    month: dayjs.Dayjs
  ): CalendarDay[] => {
    if (data.length === 0) {
      return [];
    }
    const scheduleMap: Record<string, CalendarEvent[]> = {};

    // Populate scheduleMap with events
    data.forEach(record => {
      const { date, startTime, boundEndTime, name, note, id, assign } = record;
      let onClick;
      if (planState === 'FINISHED') {
        onClick = finishedClickHandler;
      } else if (planState === 'PLANNED') {
        onClick = plannedClickHandler;
      } else {
        onClick = () => defaultClickHandler(id);
      }
      const event: CalendarEvent = {
        start: startTime,
        end: boundEndTime,
        label: name,
        tooltip: note ?? `${startTime} - ${boundEndTime}`,
        color: assign.length === 0 ? 'gray' : 'blue',
        onClick
      };

      if (!scheduleMap[date]) {
        scheduleMap[date] = [];
      }
      scheduleMap[date].push(event);
    });

    // Get all days in the month
    const daysInMonth = dayjs(month).daysInMonth();
    const fullSchedule: CalendarDay[] = [];

    for (let i = 1; i <= daysInMonth; i++) {
      const date = dayjs(month).date(i).format('YYYY-MM-DD');
      fullSchedule.push({
        date,
        events: scheduleMap[date] || [],
      });
    }

    return fullSchedule;
  };

  const handleModalClose = () => {
    closeAssignmentsModal();
    loadAllTasks(); // Reload tasks after modal is closed
  };

  return (
    <Flex
      gap="md"
      direction="row"
      align="flex-start"
      className="flex-nowrap"
      key={updateKey}
    >
      {/* Přidáváme key, který se změní při každém update a vynutí rerender */}
      <Stack className="w-56" key={updateKey}>
        {children}
      </Stack>
      <Stack className="w-full">
        {clientPlanId && clientName ? (
          <div className="flex flex-col w-full h-full">
            <div
              style={{
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: '1.2em',
              }}
            >
              {clientName}
            </div>
            <Calendar days={clientSchedule} month={month} />
          </div>
        ) : (
          <div className="flex flex-col w-full h-full">
            Vyberte Klienta pro kterého chcete zobrazit plán
          </div>
        )}
      </Stack>
      {clientPlanId !== undefined && currTaskId !== undefined && (
        <AssignmentsModal
          clientPlanId={clientPlanId}
          taskId={currTaskId}
          opened={openedAssignmentsModal}
          close={handleModalClose}
        />
      )}
    </Flex>
  );
};

export default PlanningContainer;
